import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Row,
  Col,
  Card,
  Typography,
  Input,
  Button,
  Spin,
  Empty,
  Avatar,
  Tag,
  message,
} from 'antd';
import {
  TeamOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS, AVATAR_COLORS } from '@/constants';
import { formatShortDate, formatRelative } from '@/utils/date';
import type { ApiResponse, TeacherStudentConnection } from '@/types';

const { Title, Text } = Typography;

function getTeacherAvatarColor(name: string | undefined): string {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function TeachersPage() {
  const [pendingCodes, setPendingCodes] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['student', 'teachers'],
    queryFn: () =>
      api.get<never, ApiResponse<TeacherStudentConnection[]>>(
        API_ENDPOINTS.STUDENT_TEACHERS,
      ),
  });

  const connections: TeacherStudentConnection[] = data?.data ?? [];
  const activeConnections = connections.filter((c) => c.status === 'ACTIVE');
  const pendingInvites = connections.filter((c) => c.status === 'INVITED');

  const joinMutation = useMutation({
    mutationFn: ({ code, connectionId }: { code: string; connectionId: string }) =>
      api.post<never, ApiResponse<TeacherStudentConnection>>(
        API_ENDPOINTS.STUDENT_JOIN,
        { inviteCode: code.trim(), connectionId },
      ),
    onSuccess: () => {
      message.success('Successfully joined teacher!');
      setPendingCodes({});
      queryClient.invalidateQueries({ queryKey: ['student', 'teachers'] });
    },
    onError: (error: { error?: { message?: string } }) => {
      const msg = error?.error?.message ?? 'Failed to join. Please check the code and try again.';
      message.error(msg);
    },
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          My Teachers
        </Title>
        <Text type="secondary" style={{ fontSize: 15 }}>
          Connect with your teachers and start learning together.
        </Text>
      </div>

      {pendingInvites.length > 0 && (
        <Card
          style={{ marginBottom: 24, borderRadius: 12, border: '1px solid #fde68a', background: '#fffbeb' }}
          styles={{ body: { padding: '16px 20px' } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <ClockCircleOutlined style={{ color: '#f59e0b' }} />
            <Text strong style={{ fontSize: 15 }}>Pending Invitations ({pendingInvites.length})</Text>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pendingInvites.map((invite) => {
              const teacherName = invite.teacher?.name ?? 'Teacher';
              return (
                <div
                  key={invite.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: '#fff',
                    borderRadius: 10,
                    padding: '10px 14px',
                    border: '1px solid #e5e7eb',
                    flexWrap: 'wrap',
                  }}
                >
                  <Avatar
                    size={36}
                    style={{
                      background: getTeacherAvatarColor(teacherName),
                      fontWeight: 600,
                      fontSize: 15,
                      flexShrink: 0,
                    }}
                  >
                    {teacherName.charAt(0).toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <Text strong style={{ display: 'block' }}>{teacherName}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Invited {formatRelative(invite.invitedAt)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Input
                      placeholder="Enter invite code"
                      value={pendingCodes[invite.id] ?? ''}
                      onChange={(e) =>
                        setPendingCodes((prev) => ({ ...prev, [invite.id]: e.target.value }))
                      }
                      style={{ width: 180 }}
                      maxLength={20}
                    />
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={joinMutation.isPending}
                      disabled={!(pendingCodes[invite.id] ?? '').trim()}
                      onClick={() => {
                        const code = (pendingCodes[invite.id] ?? '').trim();
                        if (code) joinMutation.mutate({ code, connectionId: invite.id });
                      }}
                    >
                      Accept
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {activeConnections.length === 0 ? (
        <Card style={{ borderRadius: 12, textAlign: 'center', padding: '40px 0' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                  No teachers yet
                </Text>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {pendingInvites.length > 0
                    ? 'You have pending invitations above — enter the invite code to connect!'
                    : 'Enter an invite code from your teacher to get started!'}
                </Text>
              </div>
            }
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {activeConnections.map((connection) => {
            const teacher = connection.teacher;
            const teacherName = teacher?.name ?? 'Teacher';
            const avatarColor = getTeacherAvatarColor(teacherName);

            return (
              <Col xs={24} sm={12} lg={8} key={connection.id}>
                <Card style={{ borderRadius: 12, height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Avatar
                      size={52}
                      style={{
                        background: avatarColor,
                        fontWeight: 600,
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {teacherName.charAt(0).toUpperCase()}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        strong
                        style={{
                          fontSize: 16,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {teacherName}
                      </Text>
                      <Tag
                        style={{
                          color: '#059669',
                          background: '#d1fae5',
                          border: 'none',
                          fontWeight: 500,
                          fontSize: 11,
                          marginTop: 4,
                        }}
                      >
                        Connected
                      </Tag>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 12,
                      borderTop: '1px solid #f0f0f0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    {teacher?.subjectIds && teacher.subjectIds.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <TeamOutlined style={{ color: '#94a3b8', fontSize: 13 }} />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {teacher.subjectIds.length} subject{teacher.subjectIds.length !== 1 ? 's' : ''}
                        </Text>
                      </div>
                    )}
                    {connection.joinedAt && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CalendarOutlined style={{ color: '#94a3b8', fontSize: 13 }} />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Connected since {formatShortDate(connection.joinedAt)}
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
}
